/**
 * useSubtitlesApi Hook
 *
 * Encapsulates all HTTP calls for the subtitles-only feature:
 *   1. POST /api/v1/subtitles/upload     — multipart upload
 *   2. POST /api/v1/subtitles/generate   — kick off job
 *   3. GET  /api/v1/jobs/{job_id}        — poll job status
 *   4. POST /api/v1/subtitles/{job_id}/cancel
 *   5. GET  /api/v1/subtitles/{job_id}/download — build download URL
 *
 * Polling uses setInterval with the POLLING_INTERVALS.NORMAL constant
 * and cleans up properly on unmount / completion / cancellation.
 */

import { useCallback, useRef, useEffect } from 'react';
import { buildApiUrl } from '@/services/urlResolver';
import {
  handleResponse,
  handleDirectResponse,
  getAuthHeadersFromStorage,
  extractErrorMessage,
} from '@/utils/httpUtils';
import { POLLING_INTERVALS, FAILURE_THRESHOLDS } from '@/constants/timings';
import type {
  SubtitleUploadResponse,
  SubtitleGenerateRequest,
  SubtitleJobResponse,
  SubtitleStyle,
} from '../types';
import type { SubtitlesStateReturn } from './useSubtitlesState';

// ============================================================================
// Types
// ============================================================================

interface UseSubtitlesApiOptions {
  actions: SubtitlesStateReturn['actions'];
  state: SubtitlesStateReturn['state'];
  onSuccess: (title: string, message: string) => void;
  onError: (title: string, message: string) => void;
}

export interface UseSubtitlesApiReturn {
  uploadAndGenerate: (options: UploadAndGenerateOptions) => Promise<void>;
  cancelJob: () => Promise<void>;
  getDownloadUrl: () => string | null;
}

export interface UploadAndGenerateOptions {
  file: File;
  sourceLanguage: string;
  targetLanguages: string[];
  outputMode: SubtitleGenerateRequest['output_mode'];
  formats: SubtitleGenerateRequest['formats'];
  useLlmRefinement: boolean;
  useKaraoke: boolean;
  /** Required for hard_burn — set to true to acknowledge rendering cost */
  confirmCost: boolean;
  /** Optional ASS style for hard_burn rendering */
  style?: SubtitleStyle;
}

// ============================================================================
// Hook
// ============================================================================

export function useSubtitlesApi({
  actions,
  state,
  onSuccess,
  onError,
}: UseSubtitlesApiOptions): UseSubtitlesApiReturn {
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const consecutiveFailuresRef = useRef(0);
  const jobIdRef = useRef<string | null>(null);

  // Keep jobIdRef in sync with state without causing re-renders
  useEffect(() => {
    jobIdRef.current = state.jobId;
  }, [state.jobId]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current !== null) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // ------------------------------------------------------------------
  // Internal: stop polling
  // ------------------------------------------------------------------
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    consecutiveFailuresRef.current = 0;
  }, []);

  // ------------------------------------------------------------------
  // Internal: poll a single tick
  // ------------------------------------------------------------------
  const pollOnce = useCallback(async () => {
    const jobId = jobIdRef.current;
    if (!jobId) return;

    try {
      const headers = await getAuthHeadersFromStorage();
      const response = await fetch(buildApiUrl(`/api/v1/jobs/${jobId}`), { headers });
      // /api/v1/jobs/{id} returns { success, data: {...} } — use handleResponse to unwrap
      const job = await handleResponse<SubtitleJobResponse>(response);

      consecutiveFailuresRef.current = 0;

      actions.setJobStatus(job.status, job.progress ?? 0, job.message ?? '');

      if (job.status === 'completed') {
        stopPolling();
        // The unified job envelope already carries the artifact's local path
        // for jobs that ran on this machine; keeping it lets the desktop app
        // reveal the file instead of re-downloading it over HTTP.
        actions.setCompleted(job.extra_metadata?.output_file_path ?? null);
        onSuccess('subtitles.result.completedTitle', 'subtitles.result.completedMessage');
      } else if (job.status === 'error' || job.status === 'failed') {
        stopPolling();
        const errorMsg = job.error ?? 'Job failed with unknown error';
        actions.setError(errorMsg);
        onError('subtitles.errors.failedTitle', errorMsg);
      }
    } catch (error) {
      consecutiveFailuresRef.current += 1;
      if (consecutiveFailuresRef.current >= FAILURE_THRESHOLDS.STOP_AFTER_FAILURES) {
        stopPolling();
        const errorMsg = extractErrorMessage(error, 'Polling failed after multiple retries');
        actions.setError(errorMsg);
        onError('subtitles.errors.pollingTitle', errorMsg);
      }
    }
  }, [actions, stopPolling, onSuccess, onError]);

  // ------------------------------------------------------------------
  // Internal: start polling
  // ------------------------------------------------------------------
  const startPolling = useCallback(() => {
    stopPolling();
    // First tick immediately
    void pollOnce();
    pollIntervalRef.current = setInterval(() => {
      void pollOnce();
    }, POLLING_INTERVALS.NORMAL);
  }, [pollOnce, stopPolling]);

  // ------------------------------------------------------------------
  // Public: upload file then kick off generation
  // ------------------------------------------------------------------
  const uploadAndGenerate = useCallback(
    async ({
      file,
      sourceLanguage,
      targetLanguages,
      outputMode,
      formats,
      useLlmRefinement,
      useKaraoke,
      confirmCost,
      style,
    }: UploadAndGenerateOptions) => {
      try {
        // 1. Upload
        actions.startUploading();

        const uploadHeaders = await getAuthHeadersFromStorage();
        // Remove Content-Type so the browser sets multipart boundary automatically
        const uploadHeadersObj = new Headers(uploadHeaders as HeadersInit);
        uploadHeadersObj.delete('Content-Type');

        const formData = new FormData();
        formData.append('file', file);

        const uploadResponse = await fetch(buildApiUrl('/api/v1/subtitles/upload'), {
          method: 'POST',
          headers: uploadHeadersObj,
          body: formData,
        });

        const uploadData = await handleDirectResponse<SubtitleUploadResponse>(uploadResponse);
        actions.setMediaUrl(uploadData.media_url, uploadData.media_type);
        actions.setUploadProgress(100);

        // 2. Generate
        const generateBody: SubtitleGenerateRequest = {
          media_url: uploadData.media_url,
          source_language: sourceLanguage,
          target_languages: targetLanguages,
          output_mode: outputMode,
          formats,
          use_llm_refinement: useLlmRefinement,
          use_karaoke: useKaraoke,
          confirm_cost: confirmCost,
          ...(style !== undefined ? { style } : {}),
        };

        const generateHeaders = await getAuthHeadersFromStorage();
        const generateResponse = await fetch(buildApiUrl('/api/v1/subtitles/generate'), {
          method: 'POST',
          headers: generateHeaders,
          body: JSON.stringify(generateBody),
        });

        const generateData = await handleDirectResponse<{ job_id: string }>(generateResponse);
        actions.startGenerating(generateData.job_id);

        // 3. Start polling
        startPolling();
      } catch (error) {
        const errorMsg = extractErrorMessage(error, 'Failed to start subtitle generation');
        actions.setError(errorMsg);
        onError('subtitles.errors.failedTitle', errorMsg);
      }
    },
    [actions, startPolling, onError]
  );

  // ------------------------------------------------------------------
  // Public: cancel current job
  // ------------------------------------------------------------------
  const cancelJob = useCallback(async () => {
    stopPolling();
    const jobId = jobIdRef.current;
    if (!jobId) {
      actions.cancel();
      return;
    }

    try {
      const headers = await getAuthHeadersFromStorage();
      await fetch(buildApiUrl(`/api/v1/subtitles/${jobId}/cancel`), {
        method: 'POST',
        headers,
      });
    } catch {
      // Best-effort cancel — ignore errors
    } finally {
      actions.cancel();
    }
  }, [actions, stopPolling]);

  // ------------------------------------------------------------------
  // Public: derive download URL (the endpoint streams a ZIP)
  // ------------------------------------------------------------------
  const getDownloadUrl = useCallback((): string | null => {
    const jobId = state.jobId;
    if (!jobId || state.status !== 'completed') return null;
    return buildApiUrl(`/api/v1/subtitles/${jobId}/download`);
  }, [state.jobId, state.status]);

  return { uploadAndGenerate, cancelJob, getDownloadUrl };
}
